import { useList } from '@refinedev/core'
import { useCustom } from '@refinedev/core'
import {
  Avatar,
  Button,
  Card,
  Empty,
  Spin,
  Typography
} from 'antd'
import {
  AppstoreOutlined,
  CopyOutlined,
  PlusOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

import { PageHeader, PageShell } from '@/components/page-shell'
import { useMessage } from '@/providers/message-provider'

const { Text, Paragraph } = Typography

/**
 * 项目货架（首页 F2）
 *
 * 卡片视图展示所有项目，点击进入项目详情。
 * 右上角"新建项目"按钮触发创建流程。
 */

interface Project {
  id: string
  code: string
  name: string
  description: string | null
  publicKeyB64: string
  itemUrl: string | null
  defaultSku: string
  maxUnbind: number
  offlineGraceDays: number
  minClientVersion: string | null
  status: 'active' | 'suspended' | 'archived'
  createdAt: string
  updatedAt: string
}

/** 项目状态徽章 */
function StatusBadge({ status }: { status: Project['status'] }) {
  const config: Record<Project['status'], { color: string; bg: string; label: string }> = {
    active: { color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)', label: '运营中' },
    suspended: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', label: '已暂停' },
    archived: { color: '#64748B', bg: 'rgba(100, 116, 139, 0.1)', label: '已归档' }
  }
  const c = config[status]
  return (
    <span
      className="status-badge"
      style={{ color: c.color, background: c.bg, border: `1px solid ${c.color}33` }}
    >
      {c.label}
    </span>
  )
}

export default function ProjectListPage() {
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useList<Project>({
    resource: 'projects',
    pagination: { mode: 'off' }
  })

  const projects = data?.data ?? []

  // 拉取跨项目统计（用于顶部概览）
  const { data: statsData, isLoading: statsLoading } = useCustom<{ totalProjects: number; activeLicenses: number; revokedLicenses: number; totalDevices: number }>({
    url: '/api/admin/stats',
    method: 'get'
  })

  if (isError) {
    return (
      <div className="app-loading-state" style={{ textAlign: 'center' }}>
        <Empty description="加载失败" />
        <Button icon={<ReloadOutlined />} onClick={() => refetch()} style={{ marginTop: 16 }}>
          重试
        </Button>
      </div>
    )
  }

  return (
    <PageShell className="page-enter" width="wide">
      <PageHeader
        title="项目货架"
        subtitle="管理你的付费软件项目，每个项目拥有独立密钥对、序列号空间和运营配置。"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => navigate('/projects/create')}
            style={{ fontWeight: 500 }}
          >
            新建项目
          </Button>
        }
      />

      {/* 顶部统计概览 */}
      {!statsLoading && statsData?.data && (
        <div className="app-stat-grid stagger-list">
          <StatCard label="项目总数" value={statsData.data.totalProjects} accent="#10B981" />
          <StatCard label="活跃序列号" value={statsData.data.activeLicenses} accent="#10B981" />
          <StatCard label="已吊销" value={statsData.data.revokedLicenses} accent="#F43F5E" />
          <StatCard label="设备总数" value={statsData.data.totalDevices} accent="#F59E0B" />
        </div>
      )}

      {/* 项目卡片网格 */}
      {isLoading ? (
        <div className="app-loading-state" style={{ textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : projects.length === 0 ? (
        <Card className="app-surface-card">
          <Empty
            image={<AppstoreOutlined style={{ fontSize: 48, color: '#CBD5E1' }} />}
            description="还没有项目，点击右上角新建第一个项目"
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/projects/create')}>
              新建项目
            </Button>
          </Empty>
        </Card>
      ) : (
        <div className="app-project-grid stagger-list">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} onClick={() => navigate(`/projects/${p.code}`)} />
          ))}
        </div>
      )}
    </PageShell>
  )
}

/** 顶部统计小卡片 */
function StatCard({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <Card size="small" className="app-stat-card app-surface-card">
      <div className="app-stat-card__label">{label}</div>
      <div className="app-stat-card__value">
        <span className="tabular-nums" style={{ color: accent }}>
          {value}
        </span>
      </div>
    </Card>
  )
}

/** 项目卡片 */
function ProjectCard({ project, onClick }: { project: Project; onClick: () => void }) {
  const message = useMessage()
  const copyCode = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(project.code)
    message.success(`已复制项目 code: ${project.code}`)
  }

  // 取 code 前 2 个字符作为头像
  const avatarText = project.code.slice(0, 2)

  return (
    <Card
      hoverable
      onClick={onClick}
      className="clickable-card app-surface-card"
      style={{ cursor: 'pointer', borderColor: 'var(--ant-color-border, #E2E8F0)' }}
      styles={{ body: { padding: 20 } }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
        <Avatar
          shape="square"
          size={44}
          style={{
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            fontWeight: 600,
            fontSize: 16
          }}
        >
          {avatarText}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 4
            }}
          >
            <Text strong style={{ fontSize: 15 }}>
              {project.name}
            </Text>
            <StatusBadge status={project.status} />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--ant-color-text-tertiary, #64748B)'
            }}
          >
            <code style={{ fontFamily: 'JetBrains Mono, monospace', color: '#10B981' }}>
              {project.code}
            </code>
            <CopyOutlined style={{ cursor: 'pointer', opacity: 0.5 }} onClick={copyCode} />
          </div>
        </div>
      </div>

      {project.description && (
        <Paragraph
          type="secondary"
          ellipsis={{ rows: 2 }}
          style={{ fontSize: 13, marginBottom: 12, minHeight: 38 }}
        >
          {project.description}
        </Paragraph>
      )}

      <div
        style={{
          display: 'flex',
          gap: 16,
          paddingTop: 12,
          borderTop: '1px solid var(--ant-color-border-secondary, #F1F5F9)',
          fontSize: 12
        }}
      >
        <ProjectMeta label="默认 SKU" value={project.defaultSku} />
        <ProjectMeta label="解绑上限" value={`${project.maxUnbind}/年`} />
        <ProjectMeta label="离线宽限" value={`${project.offlineGraceDays} 天`} />
      </div>
    </Card>
  )
}

function ProjectMeta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: 'var(--ant-color-text-tertiary, #64748B)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 500, color: 'var(--ant-color-text, #0F172A)' }}>{value}</div>
    </div>
  )
}
